<script setup>
// FLOW-06 (SPEC S4, o detalhe, R14 e H2; Task 6.0, depois 6.7) — uma execução de produção aberta:
// o que rodou, de quem, quando, quanto durou, os passos com o que a produção guarda deles, a
// resposta, as reexecuções e as ações. Recebe o detalhe do `GET /powerflow/execucoes/:id` como veio
// (o item da lista mais `duravel`, `params`, `resposta`, `reexecucoes`, `gravava_no_passo_que_falhou`
// e `passos`, cada um com `estado`, `geracao` e `grava`: PLAN G25) e só mostra: quem a usa pede,
// navega e repassa as ações (`abrirNoEditor`, `reexecutar(forma)`, `cancelar`, `abrirOrigem(id)`).
//
// **Os passos** vêm em ordem, cada um com o glifo do estado, o tipo, a duração e, ao abrir, a saída
// guardada: a produção guarda só o que o flow lê (D18), e a tela diz isso. O passo que falhou leva
// o erro de leigo (D19) logo embaixo. A retomada aparece onde aconteceu: quando a `geracao` muda de
// um passo para o seguinte, a execução caiu entre os dois e voltou em outra tarefa. Sem passo, a
// tela diz por quê, e o erro vai no cabeçalho: a execução do agente, antes da resposta, não guarda
// passo (R2); a durável na fila ou rodando ainda não terminou nenhum; e a que terminou sem passo (a
// cancelada na fila) não rodou nenhum. Depois de 30 dias, a poda apaga o detalhe (os passos, a
// entrada e a resposta), e esses painéis dizem isso, na frente de qualquer outro motivo.
//
// **Reexecutar** abre a escolha das três formas da R14: do início com a versão original, do início
// com a publicada agora e, só na durável que falhou, do passo que falhou, que vem marcado porque
// não repete o que já deu certo. Quando o passo que falhou gravava (`gravava_no_passo_que_falhou`,
// que o detalhe traz também na execução do agente, sem passos), o aviso do H2 vem antes de
// confirmar, em qualquer forma. Na execução cujo detalhe a poda já apagou, Reexecutar fica desligado,
// com o porquê embaixo: as três formas precisam da entrada e dos passos, e o servidor as recusa (409
// `detalhe_apagado`). **Cancelar** só aparece na enfileirada ou rodando, e pede confirmação. As ações
// seguem a permissão: reexecutar e cancelar são de quem executa (`podeExecutar`, quem vê a aba); abrir
// no editor, de quem monta flow (`podeEditar`), como na lista de Flows.
import { computed, ref, useId } from 'vue';
import { useI18n, I18nT } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AvisoDoFlow from '../settings/powerTools/flow/AvisoDoFlow.vue';
import { humanizar } from '../settings/powerTools/usoDoSegredo';
import {
  GLIFOS_DOS_ESTADOS,
  ICONES_DE_QUEM,
  useExecucaoNaTela,
} from './useExecucaoNaTela';

const props = defineProps({
  // O detalhe da rota, como veio.
  execucao: { type: Object, required: true },
  podeExecutar: { type: Boolean, default: false },
  podeEditar: { type: Boolean, default: false },
});

const emit = defineEmits([
  'abrirNoEditor',
  'reexecutar',
  'cancelar',
  'abrirOrigem',
]);

const { t } = useI18n();
const { idioma, rotulosDosEstados, quemChamou, haQuanto, duracao } =
  useExecucaoNaTela();

// Numa linha só no template (como no cofre): quebrado, o `{{ }}` ganha espaço dentro do <code>.
const CLASSE_DO_CODIGO =
  'px-1 font-mono rounded bg-n-alpha-black2 text-n-slate-12';
const CLASSE_DO_TECNICO = 'font-mono text-xs break-all text-n-slate-11';
const CLASSE_DO_JSON =
  'p-3 m-0 overflow-auto font-mono text-xs leading-5 whitespace-pre-wrap break-words rounded-lg max-h-64 bg-n-alpha-black2 text-n-slate-12';
const CLASSE_DO_PAINEL =
  'flex flex-col gap-3 px-5 py-4 border rounded-pz border-n-weak bg-n-card shadow-pz';

// O detalhe (os passos, a entrada e a resposta) fica guardado 30 dias, e o resumo, 90 (Gate 1, Q5).
// A poda de hora em hora (`duravel.podar`, PLAN G10) apaga os passos e zera os `params` e a
// `resposta` da execução criada há mais de 30 dias, e a linha dela fica até os 90.
const DIAS_DO_DETALHE = 30;
const DIA_EM_MS = 24 * 60 * 60 * 1000;

const execucao = computed(() => props.execucao);
const passos = computed(() => execucao.value.passos || []);
const estado = computed(() => execucao.value.estado);
const emAndamento = computed(() =>
  ['enfileirada', 'rodando'].includes(estado.value)
);

// -- Tempo
const formatoDaData = computed(
  () =>
    new Intl.DateTimeFormat(idioma.value, {
      dateStyle: 'short',
      timeStyle: 'medium',
    })
);
const dataCompleta = instante =>
  instante ? formatoDaData.value.format(new Date(instante)) : '';
const entre = (de, ate) => Date.parse(ate) - Date.parse(de);

// -- Passos
// O `caminho` de execução vira o nome do passo como quem monta o lê: `steps[resolve]{2}.steps[criar]`
// é "resolve[2] › criar" (a volta 2 do laço).
const nomeDoPasso = caminho =>
  String(caminho || '')
    .replace(/steps\[([^\]]+)\]/g, '$1')
    .replace(/\{(\d+)\}/g, '[$1]')
    .replace(/\./g, ' › ');
// O passo está em `sucesso`, `erro` ou `rodando` (G25): os glifos da execução servem.
const glifoDoPasso = passo => GLIFOS_DOS_ESTADOS[passo.estado];
const duracaoDoPasso = passo =>
  passo.terminado_em
    ? duracao(entre(passo.iniciado_em, passo.terminado_em))
    : t('POWER_TOOLS.POWERFLOW.DETAIL.IN_PROGRESS');

// Onde a execução voltou: o passo cuja `geracao` é maior que a do anterior. O tempo é o buraco entre
// o fim de um e o começo do outro.
const tempoDaRetomadaAntes = indice => {
  if (indice === 0) return '';
  const anterior = passos.value[indice - 1];
  const passo = passos.value[indice];
  if (!(passo.geracao > anterior.geracao)) return '';
  return duracao(entre(anterior.terminado_em, passo.iniciado_em));
};

const passoQueFalhou = computed(() =>
  passos.value.find(passo => passo.estado === 'erro')
);

// A execução criada há mais de 30 dias já perdeu o detalhe, pela mesma borda da poda (`criada_em`
// antes de agora menos 30 dias). O painel que ainda tem o que mostrar (a poda roda de hora em hora)
// mostra; o vazio diz que o detalhe foi apagado, e não que nunca existiu.
const detalheApagado = computed(
  () =>
    Date.now() - Date.parse(execucao.value.criada_em) >
    DIAS_DO_DETALHE * DIA_EM_MS
);
const textoDoApagado = computed(() =>
  t('POWER_TOOLS.POWERFLOW.DETAIL.PRUNED', { dias: DIAS_DO_DETALHE })
);
// Reexecutar, nas três formas, precisa da entrada e dos passos: na execução podada, o botão fica
// desligado pela mesma regra, e o porquê fica à vista, ligado a ele pelo `aria-describedby`.
const mostraReexecutar = computed(
  () => props.podeExecutar && !emAndamento.value
);
const idDoSemReexecutar = useId();
const temEntrada = computed(
  () => !!execucao.value.params && Object.keys(execucao.value.params).length > 0
);
const temResposta = computed(
  () =>
    execucao.value.resposta !== undefined && execucao.value.resposta !== null
);

// Sem passo na lista, o porquê vem dos campos do detalhe (G25). O detalhe apagado vem antes de tudo.
// Só a execução que não é durável deixa de guardar os passos: a do agente, pelo `/tools/invoke`, até
// a resposta (G8), seja qual for o `invocador` (o PowerAgents ou o agent-runner antigo). A da aba,
// da API e do MCP nasce durável e guarda cada passo que termina; sem passo, ela ainda não terminou
// nenhum (na fila ou rodando) ou terminou sem rodar nenhum (a cancelada na fila).
const textoSemPassos = computed(() => {
  if (detalheApagado.value) return textoDoApagado.value;
  if (execucao.value.duravel === false) {
    return t('POWER_TOOLS.POWERFLOW.DETAIL.STEPS.EMPTY_AGENT');
  }
  return emAndamento.value
    ? t('POWER_TOOLS.POWERFLOW.DETAIL.STEPS.EMPTY_IN_PROGRESS')
    : t('POWER_TOOLS.POWERFLOW.DETAIL.STEPS.EMPTY_FINISHED');
});

const abertos = ref([]);
const estaAberto = passo => abertos.value.includes(passo.caminho);
function alternar(passo) {
  abertos.value = estaAberto(passo)
    ? abertos.value.filter(caminho => caminho !== passo.caminho)
    : [...abertos.value, passo.caminho];
}
const comoJson = valor =>
  typeof valor === 'string' ? valor : JSON.stringify(valor, null, 2);

// O erro de leigo (D19): embaixo do passo que falhou, ou no cabeçalho quando não há passo guardado.
const mensagem = computed(() => execucao.value.mensagem || null);
const mostrarTecnico = ref(false);
const detalheTecnico = computed(() =>
  [execucao.value.desfecho, mensagem.value?.detalhe].filter(Boolean).join(' · ')
);

// -- Reexecutar (R14) e o aviso do H2
// Durável é a execução sem quem espera (aba, API, MCP) e a cauda depois do `respond`: o detalhe diz
// (`duravel`, G25), e só ela retoma do passo que falhou.
const formas = computed(() => [
  ...(estado.value === 'erro' && execucao.value.duravel
    ? ['passo_que_falhou']
    : []),
  'inicio_original',
  'inicio_publicada',
]);
const textosDasFormas = computed(() => {
  const versao = { versao: execucao.value.flow?.versao };
  return {
    passo_que_falhou: {
      titulo: t('POWER_TOOLS.POWERFLOW.DETAIL.FORMS.PASSO_QUE_FALHOU'),
      dica: t(
        'POWER_TOOLS.POWERFLOW.DETAIL.FORMS.PASSO_QUE_FALHOU_HINT',
        versao
      ),
    },
    inicio_original: {
      titulo: t('POWER_TOOLS.POWERFLOW.DETAIL.FORMS.INICIO_ORIGINAL'),
      dica: t(
        'POWER_TOOLS.POWERFLOW.DETAIL.FORMS.INICIO_ORIGINAL_HINT',
        versao
      ),
    },
    inicio_publicada: {
      titulo: t('POWER_TOOLS.POWERFLOW.DETAIL.FORMS.INICIO_PUBLICADA'),
      dica: t('POWER_TOOLS.POWERFLOW.DETAIL.FORMS.INICIO_PUBLICADA_HINT'),
    },
  };
});
const formaEscolhida = ref('inicio_original');
// H2: o servidor diz se o passo que falhou gravava (G25), também quando não há passo guardado; o
// nome vem do passo, ou do `passo_onde_parou` na execução do agente.
const gravavaNoQueFalhou = computed(
  () => execucao.value.gravava_no_passo_que_falhou === true
);
const nomeDoQueFalhou = computed(() =>
  passoQueFalhou.value
    ? nomeDoPasso(passoQueFalhou.value.caminho)
    : execucao.value.passo_onde_parou
);

const dialogoDeReexecutar = ref(null);
const dialogoDeCancelar = ref(null);
const cartoesDasFormas = ref([]);

// A primeira forma vem marcada: na durável que falhou, a do passo que falhou, que não repete o que já
// deu certo (nem a escrita de um passo de cima).
function abrirReexecutar() {
  [formaEscolhida.value] = formas.value;
  dialogoDeReexecutar.value?.open();
}
function confirmarReexecutar() {
  emit('reexecutar', formaEscolhida.value);
  dialogoDeReexecutar.value?.close();
}
function confirmarCancelar() {
  emit('cancelar');
  dialogoDeCancelar.value?.close();
}

// As setas andam pelas formas, como num grupo de rádio; o foco vai junto. O cartão é um <button>, e o
// `_base.scss` tira a borda de todo botão: o `border-solid` a devolve.
function teclado(evento) {
  const passo = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[
    evento.key
  ];
  if (!passo) return;
  evento.preventDefault();
  const total = formas.value.length;
  const atual = formas.value.indexOf(formaEscolhida.value);
  const proximo = (atual + passo + total) % total;
  formaEscolhida.value = formas.value[proximo];
  cartoesDasFormas.value[proximo]?.focus();
}

const nomeDaForma = forma => textosDasFormas.value[forma]?.titulo || forma;
const idCurto = id => String(id || '').slice(0, 8);
</script>

<template>
  <section class="flex flex-col gap-4" data-execucao-detalhe>
    <header
      class="flex flex-col gap-4 px-6 py-5 border rounded-pz border-n-weak bg-n-card shadow-pz"
    >
      <div class="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div class="flex flex-col min-w-0 gap-1.5">
          <div class="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <h2 class="mb-0 text-heading-1 text-n-slate-12">
              {{ humanizar(execucao.flow?.nome) }}
            </h2>
            <span class="font-mono text-sm text-n-slate-11">
              {{
                t('POWER_TOOLS.POWERFLOW.EXECUTIONS.VERSION', {
                  versao: execucao.flow?.versao,
                })
              }}
            </span>
          </div>
          <div
            class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-n-slate-12"
          >
            <span class="inline-flex items-center gap-1.5" data-estado>
              <span
                class="size-3.5 shrink-0"
                :class="GLIFOS_DOS_ESTADOS[estado]"
                aria-hidden="true"
              />
              {{ rotulosDosEstados[estado] }}
            </span>
            <span
              class="rounded-full size-[3px] bg-n-slate-10"
              aria-hidden="true"
            />
            <span class="inline-flex items-center gap-1.5" data-quem>
              <span
                class="size-3.5 shrink-0 text-n-slate-10"
                :class="ICONES_DE_QUEM[execucao.invocador]"
                aria-hidden="true"
              />
              {{ quemChamou(execucao) }}
            </span>
            <template v-if="execucao.conversation_id">
              <span
                class="rounded-full size-[3px] bg-n-slate-10"
                aria-hidden="true"
              />
              <span class="text-n-slate-11">
                {{
                  t('POWER_TOOLS.POWERFLOW.EXECUTIONS.CONVERSATION', {
                    numero: execucao.conversation_id,
                  })
                }}
              </span>
            </template>
            <template v-if="execucao.retomadas > 0">
              <span
                class="rounded-full size-[3px] bg-n-slate-10"
                aria-hidden="true"
              />
              <span
                class="inline-flex items-center gap-1 text-n-slate-11"
                :title="t('POWER_TOOLS.POWERFLOW.EXECUTIONS.RESUMED_HINT')"
                data-retomadas
              >
                <span
                  class="i-lucide-history size-3.5 text-n-slate-10"
                  aria-hidden="true"
                />
                {{
                  t(
                    'POWER_TOOLS.POWERFLOW.EXECUTIONS.RESUMED',
                    { count: execucao.retomadas },
                    execucao.retomadas
                  )
                }}
              </span>
            </template>
          </div>
        </div>

        <div class="flex flex-col items-end gap-1.5">
          <div class="flex flex-wrap items-center justify-end gap-2">
            <Button
              v-if="podeEditar"
              size="sm"
              variant="faded"
              color="slate"
              icon="i-lucide-square-pen"
              data-abrir-no-editor
              :label="t('POWER_TOOLS.POWERFLOW.DETAIL.OPEN_IN_EDITOR')"
              @click="emit('abrirNoEditor')"
            />
            <Button
              v-if="podeExecutar && emAndamento"
              size="sm"
              variant="faded"
              color="ruby"
              icon="i-lucide-circle-stop"
              data-cancelar
              :label="t('POWER_TOOLS.POWERFLOW.DETAIL.CANCEL')"
              @click="dialogoDeCancelar?.open()"
            />
            <Button
              v-if="mostraReexecutar"
              size="sm"
              icon="i-lucide-rotate-ccw"
              data-reexecutar
              :label="t('POWER_TOOLS.POWERFLOW.DETAIL.RERUN')"
              :disabled="detalheApagado"
              :aria-describedby="detalheApagado ? idDoSemReexecutar : undefined"
              @click="abrirReexecutar"
            />
          </div>
          <p
            v-if="mostraReexecutar && detalheApagado"
            :id="idDoSemReexecutar"
            class="max-w-xs mb-0 text-xs text-end text-n-slate-11"
            data-sem-reexecutar
          >
            {{
              t('POWER_TOOLS.POWERFLOW.DETAIL.RERUN_PRUNED', {
                dias: DIAS_DO_DETALHE,
              })
            }}
          </p>
        </div>
      </div>

      <dl
        class="grid grid-cols-2 m-0 gap-x-6 gap-y-3 sm:grid-cols-4"
        data-tempos
      >
        <div class="flex flex-col gap-0.5 min-w-0">
          <dt class="text-xs text-n-slate-11">
            {{ t('POWER_TOOLS.POWERFLOW.DETAIL.STARTED') }}
          </dt>
          <dd class="m-0 text-sm tabular-nums text-n-slate-12">
            {{
              execucao.iniciada_em
                ? dataCompleta(execucao.iniciada_em)
                : t('POWER_TOOLS.POWERFLOW.DETAIL.STILL_QUEUED')
            }}
          </dd>
        </div>
        <div class="flex flex-col gap-0.5 min-w-0">
          <dt class="text-xs text-n-slate-11">
            {{ t('POWER_TOOLS.POWERFLOW.DETAIL.FINISHED') }}
          </dt>
          <dd class="m-0 text-sm tabular-nums text-n-slate-12">
            {{
              execucao.terminada_em
                ? dataCompleta(execucao.terminada_em)
                : t('POWER_TOOLS.POWERFLOW.DETAIL.IN_PROGRESS')
            }}
          </dd>
        </div>
        <div class="flex flex-col gap-0.5 min-w-0">
          <dt class="text-xs text-n-slate-11">
            {{ t('POWER_TOOLS.POWERFLOW.DETAIL.DURATION') }}
          </dt>
          <dd class="m-0 text-sm tabular-nums text-n-slate-12">
            {{
              duracao(execucao.duracao_ms) ||
              t('POWER_TOOLS.POWERFLOW.DETAIL.IN_PROGRESS')
            }}
          </dd>
        </div>
        <div class="flex flex-col gap-0.5 min-w-0">
          <dt class="text-xs text-n-slate-11">
            {{ t('POWER_TOOLS.POWERFLOW.DETAIL.EXECUTION_ID') }}
          </dt>
          <dd class="m-0 font-mono text-xs leading-5 break-all text-n-slate-12">
            {{ execucao.execucao_id }}
          </dd>
        </div>
      </dl>

      <!-- A reexecução que veio de outra: o caminho de volta para a original. -->
      <I18nT
        v-if="execucao.origem_id"
        keypath="POWER_TOOLS.POWERFLOW.DETAIL.RERUN_OF"
        tag="p"
        class="flex flex-wrap items-center gap-1.5 mb-0 text-sm text-n-slate-11"
        data-origem
      >
        <template #execucao>
          <Button
            variant="link"
            size="sm"
            color="slate"
            icon="i-lucide-arrow-up-right"
            trailing-icon
            class="font-mono"
            :label="idCurto(execucao.origem_id)"
            @click="emit('abrirOrigem', execucao.origem_id)"
          />
        </template>
      </I18nT>

      <!-- Sem passo guardado (o agente antes da resposta), o erro de leigo fica aqui, na cor do estado:
         âmbar na interrompida, rubi no erro. -->
      <AvisoDoFlow
        v-if="mensagem && !passoQueFalhou && !passos.length"
        :nivel="estado === 'interrompida' ? 'aviso' : 'erro'"
        data-erro-da-execucao
      >
        <p class="m-0 text-sm font-medium">{{ mensagem.mensagem }}</p>
        <I18nT
          v-if="execucao.passo_onde_parou"
          keypath="POWER_TOOLS.POWERFLOW.EXECUTIONS.STOPPED_AT"
          tag="p"
          class="m-0 text-sm text-n-slate-11"
        >
          <template #passo>
            <code :class="CLASSE_DO_CODIGO">{{
              execucao.passo_onde_parou
            }}</code>
          </template>
        </I18nT>
        <p v-if="mensagem.como_resolver" class="m-0 text-sm text-n-slate-11">
          {{
            t('POWER_TOOLS.POWERFLOW.DETAIL.HOW_TO_FIX', {
              texto: mensagem.como_resolver,
            })
          }}
        </p>
      </AvisoDoFlow>
    </header>

    <div class="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem] xl:items-start">
      <section :class="CLASSE_DO_PAINEL" data-passos>
        <header class="flex flex-col gap-0.5">
          <h3 class="mb-0 text-heading-2 text-n-slate-12">
            {{ t('POWER_TOOLS.POWERFLOW.DETAIL.STEPS.TITLE') }}
          </h3>
          <p class="mb-0 text-xs text-n-slate-11">
            {{ t('POWER_TOOLS.POWERFLOW.DETAIL.STEPS.NOTE') }}
          </p>
        </header>

        <p
          v-if="!passos.length"
          class="py-4 mb-0 text-sm text-center text-n-slate-11"
          data-sem-passos
        >
          {{ textoSemPassos }}
        </p>

        <ol v-else class="flex flex-col p-0 m-0 list-none">
          <li
            v-for="(passo, indice) in passos"
            :key="passo.caminho"
            :data-passo="passo.caminho"
          >
            <!-- A marca da retomada, entre o último passo antes da queda e o primeiro depois. -->
            <div
              v-if="tempoDaRetomadaAntes(indice)"
              class="flex items-start gap-3 py-2 mb-1 text-xs border-y border-dashed border-n-weak text-n-slate-11"
              data-retomada-aqui
            >
              <span class="grid w-5 shrink-0 place-items-center">
                <span
                  class="i-lucide-history size-3.5 text-n-slate-10"
                  aria-hidden="true"
                />
              </span>
              <span>
                {{
                  t('POWER_TOOLS.POWERFLOW.DETAIL.STEPS.RESUMED_HERE', {
                    tempo: tempoDaRetomadaAntes(indice),
                  })
                }}
              </span>
            </div>

            <div class="flex gap-3">
              <div class="flex flex-col items-center w-5 shrink-0 pt-2">
                <span
                  class="size-3.5 shrink-0"
                  :class="glifoDoPasso(passo)"
                  aria-hidden="true"
                />
                <span
                  v-if="indice < passos.length - 1"
                  class="flex-1 w-px mt-1.5 bg-n-weak"
                  aria-hidden="true"
                />
              </div>

              <div class="flex flex-col flex-1 min-w-0 gap-2 pb-2">
                <button
                  type="button"
                  class="flex flex-wrap items-center w-full gap-x-2 gap-y-0.5 px-2 py-1.5 -mx-2 rounded-lg text-start transition-colors duration-150 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                  :aria-expanded="estaAberto(passo) ? 'true' : 'false'"
                  data-abrir-passo
                  @click="alternar(passo)"
                >
                  <span class="font-mono text-sm truncate text-n-slate-12">
                    {{ nomeDoPasso(passo.caminho) }}
                  </span>
                  <span class="text-xs text-n-slate-11">{{ passo.tipo }}</span>
                  <span
                    v-if="passo.grava"
                    class="inline-flex items-center gap-1 text-xs text-n-slate-11"
                    data-grava
                  >
                    <span
                      class="i-lucide-pencil-line size-3 text-n-slate-10"
                      aria-hidden="true"
                    />
                    {{ t('POWER_TOOLS.POWERFLOW.DETAIL.STEPS.WRITES') }}
                  </span>
                  <span
                    class="text-xs tabular-nums ms-auto text-n-slate-11"
                    :class="{ 'text-n-slate-12': !passo.terminado_em }"
                  >
                    {{ duracaoDoPasso(passo) }}
                  </span>
                  <span
                    class="i-lucide-chevron-down size-3.5 text-n-slate-10 transition-transform duration-150"
                    :class="{ 'rotate-180': estaAberto(passo) }"
                    aria-hidden="true"
                  />
                </button>

                <AvisoDoFlow
                  v-if="passo === passoQueFalhou && mensagem"
                  nivel="erro"
                  data-erro-do-passo
                >
                  <p class="m-0 text-sm font-medium">{{ mensagem.mensagem }}</p>
                  <p
                    v-if="mensagem.como_resolver"
                    class="m-0 text-sm text-n-slate-11"
                  >
                    {{
                      t('POWER_TOOLS.POWERFLOW.DETAIL.HOW_TO_FIX', {
                        texto: mensagem.como_resolver,
                      })
                    }}
                  </p>
                  <button
                    v-if="detalheTecnico"
                    type="button"
                    class="p-0 text-xs font-medium bg-transparent text-n-slate-11 hover:text-n-slate-12 hover:underline"
                    :aria-expanded="mostrarTecnico ? 'true' : 'false'"
                    @click="mostrarTecnico = !mostrarTecnico"
                  >
                    {{
                      mostrarTecnico
                        ? t('POWER_TOOLS.POWERFLOW.DETAIL.TECHNICAL_HIDE')
                        : t('POWER_TOOLS.POWERFLOW.DETAIL.TECHNICAL_SHOW')
                    }}
                  </button>
                  <template v-if="mostrarTecnico">
                    <code :class="CLASSE_DO_TECNICO">{{ detalheTecnico }}</code>
                  </template>
                </AvisoDoFlow>

                <div
                  v-if="estaAberto(passo)"
                  class="flex flex-col gap-1.5"
                  data-saida-do-passo
                >
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ t('POWER_TOOLS.POWERFLOW.DETAIL.STEPS.OUTPUT') }}
                  </span>
                  <pre
                    v-if="passo.saida !== undefined && passo.saida !== null"
                    :class="CLASSE_DO_JSON"
                    >{{ comoJson(passo.saida) }}</pre>
                  <p v-else class="mb-0 text-xs text-n-slate-11">
                    {{ t('POWER_TOOLS.POWERFLOW.DETAIL.STEPS.NO_OUTPUT') }}
                  </p>
                  <template v-if="passo.erro">
                    <span class="text-xs font-medium text-n-slate-11">
                      {{ t('POWER_TOOLS.POWERFLOW.DETAIL.STEPS.ERROR') }}
                    </span>
                    <pre :class="CLASSE_DO_JSON">{{
                      comoJson(passo.erro)
                    }}</pre>
                  </template>
                </div>
              </div>
            </div>
          </li>
        </ol>
      </section>

      <div class="flex flex-col gap-4">
        <section :class="CLASSE_DO_PAINEL" data-entrada>
          <h3 class="mb-0 text-heading-2 text-n-slate-12">
            {{ t('POWER_TOOLS.POWERFLOW.DETAIL.INPUT.TITLE') }}
          </h3>
          <pre v-if="temEntrada" :class="CLASSE_DO_JSON">{{
            comoJson(execucao.params)
          }}</pre>
          <p v-else class="mb-0 text-sm text-n-slate-11" data-sem-entrada>
            {{
              detalheApagado
                ? textoDoApagado
                : t('POWER_TOOLS.POWERFLOW.DETAIL.INPUT.EMPTY')
            }}
          </p>
        </section>

        <section
          v-if="temResposta || detalheApagado"
          :class="CLASSE_DO_PAINEL"
          data-resposta
        >
          <h3 class="mb-0 text-heading-2 text-n-slate-12">
            {{ t('POWER_TOOLS.POWERFLOW.DETAIL.RESPONSE.TITLE') }}
          </h3>
          <pre v-if="temResposta" :class="CLASSE_DO_JSON">{{
            comoJson(execucao.resposta)
          }}</pre>
          <p v-else class="mb-0 text-sm text-n-slate-11" data-sem-resposta>
            {{ textoDoApagado }}
          </p>
        </section>

        <section
          v-if="execucao.reexecucoes?.length"
          :class="CLASSE_DO_PAINEL"
          data-reexecucoes
        >
          <h3 class="mb-0 text-heading-2 text-n-slate-12">
            {{ t('POWER_TOOLS.POWERFLOW.DETAIL.RERUNS.TITLE') }}
          </h3>
          <ul class="flex flex-col gap-2.5 p-0 m-0 list-none">
            <li
              v-for="reexecucao in execucao.reexecucoes"
              :key="reexecucao.em"
              class="flex flex-col gap-0.5"
            >
              <span class="flex items-center gap-1.5 text-sm text-n-slate-12">
                <span
                  class="i-lucide-rotate-ccw size-3.5 shrink-0 text-n-slate-10"
                  aria-hidden="true"
                />
                {{ nomeDaForma(reexecucao.forma) }}
              </span>
              <span class="text-xs ps-5 text-n-slate-11">
                {{ quemChamou(reexecucao) }}
                <span
                  class="inline-block mx-1 align-middle rounded-full size-[3px] bg-n-slate-10"
                  aria-hidden="true"
                />
                <time
                  :datetime="reexecucao.em"
                  :title="dataCompleta(reexecucao.em)"
                >
                  {{ haQuanto(reexecucao.em) }}
                </time>
              </span>
            </li>
          </ul>
        </section>
      </div>
    </div>

    <Dialog
      ref="dialogoDeReexecutar"
      width="xl"
      :title="t('POWER_TOOLS.POWERFLOW.DETAIL.RERUN_DIALOG.TITLE')"
      :description="t('POWER_TOOLS.POWERFLOW.DETAIL.RERUN_DIALOG.DESCRIPTION')"
      :confirm-button-label="t('POWER_TOOLS.POWERFLOW.DETAIL.RERUN')"
      data-dialogo-reexecutar
      @confirm="confirmarReexecutar"
    >
      <div
        role="radiogroup"
        :aria-label="t('POWER_TOOLS.POWERFLOW.DETAIL.RERUN_DIALOG.TITLE')"
        class="flex flex-col gap-2"
        @keydown="teclado"
      >
        <button
          v-for="forma in formas"
          ref="cartoesDasFormas"
          :key="forma"
          type="button"
          role="radio"
          :aria-checked="forma === formaEscolhida ? 'true' : 'false'"
          :tabindex="forma === formaEscolhida ? 0 : -1"
          class="flex items-start gap-3 px-4 py-3 border border-solid rounded-xl text-start transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-n-brand"
          :class="
            forma === formaEscolhida
              ? 'border-n-brand bg-n-alpha-2'
              : 'border-n-weak hover:border-n-strong'
          "
          :data-forma="forma"
          @click="formaEscolhida = forma"
        >
          <span
            class="grid mt-0.5 border rounded-full size-4 shrink-0 place-items-center"
            :class="
              forma === formaEscolhida ? 'border-n-brand' : 'border-n-slate-8'
            "
            aria-hidden="true"
          >
            <span
              v-if="forma === formaEscolhida"
              class="rounded-full size-2 bg-n-brand"
            />
          </span>
          <span class="flex flex-col gap-0.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ textosDasFormas[forma].titulo }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ textosDasFormas[forma].dica }}
            </span>
          </span>
        </button>
      </div>

      <!-- H2: a escrita do passo que falhou pode ter chegado do outro lado; a reexecução manda de novo. -->
      <AvisoDoFlow
        v-if="gravavaNoQueFalhou"
        nivel="aviso"
        class="mt-4"
        data-aviso-h2
      >
        <I18nT
          keypath="POWER_TOOLS.POWERFLOW.DETAIL.RERUN_DIALOG.WRITE_WARNING"
          tag="p"
          class="m-0"
        >
          <template #passo>
            <code :class="CLASSE_DO_CODIGO">{{ nomeDoQueFalhou }}</code>
          </template>
        </I18nT>
      </AvisoDoFlow>
    </Dialog>

    <Dialog
      ref="dialogoDeCancelar"
      type="alert"
      :title="t('POWER_TOOLS.POWERFLOW.DETAIL.CANCEL_DIALOG.TITLE')"
      :description="t('POWER_TOOLS.POWERFLOW.DETAIL.CANCEL_DIALOG.DESCRIPTION')"
      :confirm-button-label="
        t('POWER_TOOLS.POWERFLOW.DETAIL.CANCEL_DIALOG.CONFIRM')
      "
      :cancel-button-label="
        t('POWER_TOOLS.POWERFLOW.DETAIL.CANCEL_DIALOG.BACK')
      "
      data-dialogo-cancelar
      @confirm="confirmarCancelar"
    />
  </section>
</template>
